import { AppRoutes } from "@/constants/routes";
import { Flex, HStack, Link, Spinner } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { Permiso } from "@/pages/role-asignation/schema/role-assignation.schema";
import { PermissionCode } from "@/constants/permissions";
import {
  RequestClass,
  usePendingRequestsQuantity,
} from "@/hooks/usePendingRequests";
import { StandardAlertBadge } from "@/components/badge/StandardAlertBadge";
import { useEffect, useState } from "react";
import { AppColors } from "@/styles/colors";

interface Props {
  permissions: Permiso[]; // { codigo: string, tipo: "R" | "W" }
}

export const RequestsView = ({ permissions }: Props) => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);
  const navigate = useNavigate();

  const hasAccess = (codigo: string) =>
    permissions?.some((p) => p.codigo === codigo && p.tipoPermiso === "R");

  const canUseCashRequest = hasAccess(PermissionCode.CASH_REQUEST);
  const canUseCheckRequest = hasAccess(PermissionCode.CHECK_REQUEST);
  const canUseTransferRequest = hasAccess(PermissionCode.TRANSFER_REQUEST);
  const canUseScheduledTransfer = hasAccess(PermissionCode.SCHEDULED_TRANSFER);
  const canUseBackupChecks = hasAccess(PermissionCode.BACKUP_CHECKS);
  const canUseExternalTransfer = hasAccess(PermissionCode.EXTERNAL_TRANSFER);

  const {
    pendingRequestsQuantity,
    loadingPendingRequestsQuantity,
    pendingRequestsQuantityError,
  } = usePendingRequestsQuantity(RequestClass.FINANCIAL, isActive);

  const getBadgeContent = (type: "cheque" | "efectivo") => {
    if (pendingRequestsQuantityError) return null;
    if (loadingPendingRequestsQuantity)
      return <Spinner size="xs" role="progressbar" aria-label="loading" />;
    return Number(pendingRequestsQuantity?.[type]) > 99 ? "+99" : Number(pendingRequestsQuantity?.[type]);
  };

  const checkBadgeContent = getBadgeContent("cheque");
  const cashBadgeContent = getBadgeContent("efectivo");

  return (
    <Flex flexDir="column" paddingX={2} paddingY={1} gap="10px">
      {canUseCheckRequest && (
        <HStack alignItems="center" spaceX={1}>
          <Link
            variant="plain"
            onClick={() => navigate(AppRoutes.CHECK_REQUESTS)}
          >
            Cheque
          </Link>
          <StandardAlertBadge
            bgColor={AppColors.ERROR_TYPE_2}
            textColor={AppColors.WHITE_BORDER_1}
            content={checkBadgeContent}
          />
        </HStack>
      )}

      {canUseCashRequest && (
        <HStack alignItems="center" spaceX={1}>
          <Link
            variant="plain"
            onClick={() => navigate(AppRoutes.CASH_REQUESTS)}
          >
            Efectivo
          </Link>
          <StandardAlertBadge
            bgColor={AppColors.ERROR_TYPE_2}
            textColor={AppColors.WHITE_BORDER_1}
            content={cashBadgeContent}
          />
        </HStack>
      )}
      {canUseTransferRequest && (
        <Link
          variant="plain"
          onClick={() => navigate(AppRoutes.TRANSFER_REQUESTS)}
        >
          Transferencias
        </Link>
      )}
      {canUseScheduledTransfer && (
        <Link
          variant="plain"
          onClick={() => navigate(AppRoutes.SCHEDULED_TRANSFER_REQUESTS)}
        >
          Transferencias programadas
        </Link>
      )}
      {canUseExternalTransfer && (
        <Link
          variant="plain"
          onClick={() => navigate(AppRoutes.EXTERNAL_TRANSFER_REQUESTS)}
        >
          Transferencias externas
        </Link>
      )}
      {
        canUseBackupChecks && (
          <Link
            variant="plain"
            onClick={() => navigate(AppRoutes.BACKUP_CHECKS)}
          >
            Cheques respaldo
          </Link>
        )
      }
    </Flex>
  );
};
